import { useState } from "react";
import { SectionCard } from "shared/app/SectionCard";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { useFormTyped } from "shared/hooks";
import { Row } from "views/applications-id-report/components/Row";
import { AmenitiesForm } from "shared/app/AmenitiesForm";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { PropertyKind } from "shared/types";
import { Property } from "types/landlord/address";

const getYesNo = (value: boolean) => (value ? "Yes" : "No");

const AmenitiesList = ({ amenities }: { amenities: Property["amenities"] }) => {
  if (!amenities.length)
    return <p className="font-medium text-content-1000">-</p>;

  return (
    <div className="flex flex-wrap gap-2">
      {amenities.map((amenity) => (
        <Badge key={amenity.id} variant={BadgeVariant.BASE} size={BadgeSize.SM}>
          {amenity.title}
        </Badge>
      ))}
    </div>
  );
};

interface EditAmenitiesFormProps {
  onClose: () => void;
  initialValues: {
    dogs_allowed: boolean;
    cats_allowed: boolean;
    amenities: number[];
  };
  propertyId: number;
  level: "property" | "unit";
}

const EditAmenitiesForm = ({
  onClose,
  initialValues,
  propertyId,
  level
}: EditAmenitiesFormProps) => {
  const queryClient = useQueryClient();
  const { register, onSubmit, values } = useFormTyped(initialValues);

  const { mutate: updateAmenities, isPending } = useMutation({
    mutationFn: () => {
      return addressApi.editProperty({
        propertyId,
        pet_policy: {
          cats_allowed: values.cats_allowed,
          dogs_allowed: values.dogs_allowed
        },
        amenity_ids: values.amenities
      });
    },
    onSuccess: () => {
      toast.success("Amenities updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update amenities: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: () => {
      updateAmenities();
    }
  });

  return (
    <AmenitiesForm
      level={level}
      register={register}
      selectedAmenities={values.amenities}
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveDisabled={isPending}
          containerClassName="flex gap-2 pt-6"
        />
      }
    />
  );
};

interface AmenitiesSectionProps {
  amenities: Property["amenities"];
  propertyId: number;
  petPolicy?: {
    dogs_allowed?: boolean;
    cats_allowed?: boolean;
  };
  propertyKind: PropertyKind | null;
}

export const AmenitiesSection = ({
  amenities,
  propertyId,
  petPolicy,
  propertyKind = PropertyKind.MultiFamily
}: AmenitiesSectionProps) => {
  const [isEditting, setIsEditting] = useState(false);
  const level =
    propertyKind === PropertyKind.SingleFamily ? "unit" : "property";

  if (isEditting)
    return (
      <EditAmenitiesForm
        level={level}
        onClose={() => setIsEditting(false)}
        initialValues={{
          dogs_allowed: Boolean(petPolicy?.dogs_allowed),
          cats_allowed: Boolean(petPolicy?.cats_allowed),
          amenities: amenities.map((amenity) => amenity.id)
        }}
        propertyId={propertyId}
      />
    );

  return (
    <SectionCard
      title="Amenities"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditting(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4 w-full">
        <Row
          title="Cats allowed"
          value={getYesNo(Boolean(petPolicy?.cats_allowed))}
        />
        <Row
          title="Dogs allowed"
          value={getYesNo(Boolean(petPolicy?.dogs_allowed))}
        />
        <Row
          title="Amenities"
          value={<AmenitiesList amenities={amenities} />}
          valueClassName="w-[500px]"
        />
      </div>
    </SectionCard>
  );
};
